/**
 * GPX Data Transformers
 * Convert between different GPS data formats (GeoJSON, path arrays, etc.)
 */

interface GpxData {
  path: [number, number][];
  distance: number;
  center?: [number, number];
  timestamps?: (string | Date)[];
  elevationGain?: number;
  elevationLoss?: number;
  duration?: number;
  date?: string;
  avgSpeed?: number;
  maxSpeed?: number;
  avgPace?: number;
  maxPace?: number;
  avgHeartRate?: number;
  maxHeartRate?: number;
  samples?: {
    coordinate?: [number, number];
    timestamp?: string;
    elevation?: number;
    cumulativeDistance?: number;
    speed?: number;
    pace?: number;
    heartRate?: number;
  }[];
  startPoint?: [number, number];
  endPoint?: [number, number];
}

/**
 * Parse FeatureCollection from trail data into usable GPX data
 * @param featureCollection GeoJSON FeatureCollection object
 * @returns Parsed GPX data or null if invalid
 */
export const parseFeatureCollection = (
  featureCollection: any,
): GpxData | null => {
  if (
    !featureCollection ||
    featureCollection.type !== "FeatureCollection" ||
    !featureCollection.features?.[0]
  ) {
    return null;
  }

  const feature = featureCollection.features[0];
  if (feature.geometry?.type !== "LineString") return null;

  const path: [number, number][] = feature.geometry.coordinates.map(
    ([lon, lat]: [number, number]) => [lat, lon] as [number, number],
  );

  if (path.length === 0) return null;

  const avgLat = path.reduce((sum:any, p:any) => sum + p[0], 0) / path.length;
  const avgLon = path.reduce((sum:any, p:any) => sum + p[1], 0) / path.length;
  const properties = feature.properties || {};
  const savedSamples = properties.samples;
  const compactSamples = properties.trackSamples;
  const timestamps = properties.timestamps;
  const samples = Array.isArray(savedSamples)
    ? savedSamples
    : compactSamples
      ? path.map((coordinate, index) => ({
          coordinate,
          ...(timestamps?.[index] ? { timestamp: timestamps[index] } : {}),
          ...(compactSamples.elevation?.[index] != null ? { elevation: compactSamples.elevation[index] } : {}),
          ...(compactSamples.cumulativeDistance?.[index] != null ? { cumulativeDistance: compactSamples.cumulativeDistance[index] } : {}),
          ...(compactSamples.speed?.[index] != null ? { speed: compactSamples.speed[index] } : {}),
          ...(compactSamples.pace?.[index] != null ? { pace: compactSamples.pace[index] } : {}),
          ...(compactSamples.heartRate?.[index] != null ? { heartRate: compactSamples.heartRate[index] } : {}),
        }))
      : undefined;

  return {
    path,
    center: [avgLat, avgLon],
    timestamps,
    distance: properties.distance || 0,
    elevationGain: properties.elevationGain,
    elevationLoss: properties.elevationLoss,
    duration: properties.duration,
    date: properties.date,
    avgSpeed: properties.avgSpeed,
    maxSpeed: properties.maxSpeed,
    avgPace: properties.avgPace,
    maxPace: properties.maxPace,
    avgHeartRate: properties.avgHeartRate,
    maxHeartRate: properties.maxHeartRate,
    samples,
  };
};

/**
 * Create GeoJSON FeatureCollection from GPX data for storage
 * @param gpxData Parsed GPX data
 * @returns GeoJSON FeatureCollection or undefined if no data
 */
export const createGeoJSONFeatureCollection = (gpxData: GpxData | null) => {
  if (!gpxData) return undefined;

  const coordinates = gpxData.path.map((p: [number, number]) => [p[1], p[0]]);
  const samples = gpxData.samples;
  const trackSamples = samples
    ? {
        elevation: samples.map((sample) => sample.elevation ?? null),
        cumulativeDistance: samples.map((sample) => sample.cumulativeDistance ?? null),
        speed: samples.map((sample) => sample.speed ?? null),
        pace: samples.map((sample) => sample.pace ?? null),
        heartRate: samples.map((sample) => sample.heartRate ?? null),
      }
    : undefined;

  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: coordinates,
        },
        properties: {
          timestamps: samples
            ? samples.map((sample) => sample.timestamp || "")
            : gpxData.timestamps,
          distance: gpxData.distance,
          duration: gpxData.duration,
          date: gpxData.date,
          elevationGain: gpxData.elevationGain,
          elevationLoss: gpxData.elevationLoss,
          avgSpeed: gpxData.avgSpeed,
          maxSpeed: gpxData.maxSpeed,
          avgPace: gpxData.avgPace,
          maxPace: gpxData.maxPace,
          avgHeartRate: gpxData.avgHeartRate,
          maxHeartRate: gpxData.maxHeartRate,
          ...(trackSamples ? { trackSamples } : {}),
        },
      },
    ],
  };
};

/**
 * Convert coordinates to GeoJSON Point
 * @param coords [latitude, longitude]
 * @returns GeoJSON Point object with proper tuple type
 */
export const coordsToGeoJSONPoint = (coords: [number, number]) => {
  return {
    type: "Point" as const,
    coordinates: [coords[1], coords[0]] as [number, number], // [lon, lat] for GeoJSON with tuple type
  };
};
